import { FormEvent, useState } from "react";
import { Trash2 } from "lucide-react";
import { Membership, MemberRole } from "@shared/types";
import { api, ApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface MembersPanelProps {
  slug: string;
  members: Membership[];
  canManage: boolean;
  onMembersChange: (members: Membership[]) => void;
}

export function MembersPanel({ slug, members, canManage, onMembersChange }: MembersPanelProps) {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>("editor");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function add(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api.addMember(slug, email, role);
      onMembersChange(res.members);
      setEmail("");
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to add member");
    } finally {
      setBusy(false);
    }
  }

  async function remove(email: string) {
    setError(null);
    try {
      const res = await api.removeMember(slug, email);
      onMembersChange(res.members);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Failed to remove member");
    }
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Members</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <ul className="divide-y rounded-lg border">
          {members.map((m) => (
            <li key={m.email} className="flex items-center justify-between p-3">
              <div className="flex items-center gap-2">
                <span className="text-sm">{m.email}</span>
                <Badge variant={m.role === "owner" ? "default" : "secondary"}>{m.role}</Badge>
                {!m.userId && (
                  <Badge variant="outline" title="Will activate when they register with this email">
                    pending
                  </Badge>
                )}
              </div>
              {canManage && (
                <Button
                  size="icon"
                  variant="ghost"
                  aria-label={`Remove ${m.email}`}
                  onClick={() => void remove(m.email)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>

        {canManage ? (
          <form onSubmit={add} className="flex flex-wrap items-end gap-3">
            <div className="flex-1 space-y-2">
              <Label htmlFor="member-email">Add member by email</Label>
              <Input
                id="member-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="colleague@clinic.com"
                required
              />
            </div>
            <div className="w-32 space-y-2">
              <Label>Role</Label>
              <Select value={role} onValueChange={(v) => setRole(v as MemberRole)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="editor">Editor</SelectItem>
                  <SelectItem value="owner">Owner</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button type="submit" disabled={busy}>
              Add
            </Button>
            <p className="w-full text-xs text-muted-foreground">
              If they haven&apos;t registered yet, they&apos;ll show as “pending” and get access
              automatically once they sign up with this email.
            </p>
          </form>
        ) : (
          <p className="text-sm text-muted-foreground">Only owners can manage members.</p>
        )}
        {error && <p className="text-sm text-destructive">{error}</p>}
      </CardContent>
    </Card>
  );
}
